<mat-toolbar class="toolbar top element-shadow">
  <div class="toolbar-left">
    <button class="icon-btn toolbar-back-btn" (click)="cancel()" matTooltip="{{ 'back' | transloco }}">
      <q-svg-wrapper iconPath="project-icons/arrow" width="24" height="24" viewBox="0 0 24 24"></q-svg-wrapper>
    </button>
    <img class="logo" src="assets/vectors/logo.svg" alt="QuestAtlas" routerLink="/" />
    <span class="toolbar-divider"></span>
    <span class="main-heading-text">{{ (tripId() ? 'editTrip' : 'createNewTrip') | transloco }}</span>
  </div>
  <div class="toolbar-actions">
    <button class="secondary-bar-btn" (click)="cancel()">{{ 'cancel' | transloco }}</button>
    <button class="primary-bar-btn" (click)="saveTrip()" [disabled]="isSaving() || tripForm.invalid">
      {{ (isSaving() ? 'saving' : 'save') | transloco }}
    </button>
  </div>
</mat-toolbar>

<div class="trip-editor-container" *transloco="let t">

  @if (trip.isLoading()) {
    <div class="loading-container">
      <mat-spinner></mat-spinner>
    </div>
  }

  @if (trip.error()) {
    <div class="error-container">
      <p class="error-message">{{ t('errorLoadingTrip') }}</p>
    </div>
  }

  @if (!trip.isLoading() && !trip.error()) {
    <div class="editor-content">
      <!-- Left Side: Form Fields -->
      <div class="form-section">
        <form [formGroup]="tripForm">
          <!-- Trip Basic Info -->
          <div class="form-card">
            <h2>{{ t('tripInformation') }}</h2>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ t('name') }}</mat-label>
              <input matInput formControlName="name" required>
              @if (tripForm.get('name')?.hasError('required') && tripForm.get('name')?.touched) {
                <mat-error>{{ t('nameRequired') }}</mat-error>
              }
            </mat-form-field>

            <mat-form-field appearance="outline" class="full-width">
              <mat-label>{{ t('description') }}</mat-label>
              <textarea matInput formControlName="description" rows="3"></textarea>
            </mat-form-field>

            <!-- Title Image -->
            <div class="title-image-section">
              <label class="section-label">{{ t('titleImage') }}</label>

              @if (!tripForm.get('titleImageUrl')?.value) {
                <div class="title-image-actions">
                  <input #titleImageFileInput type="file" accept="image/*" (change)="uploadTitleImageFile($event)" style="display: none;">

                  <button class="secondary-bar-btn small-btn" type="button" (click)="titleImageFileInput.click()">
                    <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="16" height="16" viewBox="0 0 24 24"></q-svg-wrapper>
                    {{ t('uploadImage') }}
                  </button>

                  <span class="paste-hint" (click)="pasteTitleImageFromClipboard()">
                    {{ t('pasteImage') }}
                  </span>
                </div>
              } @else {
                <div class="title-image-container">
                  <img [src]="tripForm.get('titleImageUrl')?.value"
                       [alt]="t('titleImage')">
                  <button class="icon-btn-danger remove-title-image" type="button" (click)="removeTitleImage()" [matTooltip]="t('delete')">
                    <q-svg-wrapper iconPath="project-icons/x" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                  </button>
                </div>
              }
            </div>

            <mat-slide-toggle formControlName="isPublic">
              {{ t('public') }}
            </mat-slide-toggle>

            <!-- Activity Tags -->
            <div class="activity-tags-section">
              <label class="section-label">{{ t('activityTypes') }}</label>
              <p class="section-hint">{{ t('selectActivitiesInTrip') }}</p>

              <div class="activity-tags-grid">
                @for (activity of activityTypes; track activity.value) {
                  <div class="activity-tag-chip"
                       [class.selected]="isActivityTagSelected(activity.value)"
                       (click)="toggleActivityTag(activity.value)">
                    <span class="activity-tag-label">{{ t(activity.label) }}</span>
                  </div>
                }
              </div>
            </div>
          </div>

          <!-- Trip Statistics -->
          <div class="form-card" formGroupName="stats">
            <h2>{{ t('statistics') }}</h2>

            <div class="stats-grid">
              <mat-form-field appearance="outline">
                <mat-label>{{ t('totalLength') }} (m)</mat-label>
                <input matInput type="number" formControlName="totalLength">
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ t('totalTime') }} (min)</mat-label>
                <input matInput type="number" formControlName="totalTime">
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ t('elevationGain') }} (m)</mat-label>
                <input matInput type="number" formControlName="elevationGain">
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ t('elevationLoss') }} (m)</mat-label>
                <input matInput type="number" formControlName="elevationLoss">
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ t('pavedRoads') }} (%)</mat-label>
                <input matInput type="number" min="0" max="100" formControlName="pavedRoadsPercent">
              </mat-form-field>

              <mat-form-field appearance="outline">
                <mat-label>{{ t('unpavedRoads') }} (%)</mat-label>
                <input matInput type="number" min="0" max="100" formControlName="unpavedRoadsPercent">
              </mat-form-field>
            </div>
          </div>

          <!-- Locations -->
          <div class="form-card">
            <div class="card-header">
              <h2>{{ t('locations') }}</h2>
              <button class="primary-bar-btn small-btn" (click)="addLocation()" type="button">
                <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                {{ t('addLocation') }}
              </button>
            </div>

            <!-- Location Tabs -->
            <div class="location-tabs">
              @for (location of locations.controls; track $index) {
                <div
                  class="location-tab"
                  [class.active]="selectedLocationIndex() === $index"
                  (click)="selectLocation($index)">
                  <span>{{ location.get('name')?.value || t('location') + ' ' + ($index + 1) }}</span>
                  @if (locations.length > 1) {
                    <button class="remove-tab-btn" (click)="removeLocation($index); $event.stopPropagation()" type="button">
                      <q-svg-wrapper iconPath="project-icons/close" width="16" height="16" viewBox="0 0 24 24"></q-svg-wrapper>
                    </button>
                  }
                </div>
              }
            </div>

            <!-- Selected Location Details -->
            @if (locations.length > 0 && selectedLocationIndex() < locations.length) {
              <div [formGroup]="getLocationFormGroup(selectedLocationIndex())" class="location-details">
                <mat-form-field appearance="outline" class="full-width">
                  <mat-label>{{ t('locationName') }}</mat-label>
                  <input matInput formControlName="name" required>
                </mat-form-field>

                <!-- Sections -->
                <div class="sections-container">
                  <div class="section-header">
                    <h3>{{ t('sections') }}</h3>
                    <button class="primary-bar-btn small-btn" (click)="addSection(selectedLocationIndex())" type="button">
                      <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                      {{ t('addSection') }}
                    </button>
                  </div>

                  @for (section of getLocationSections(selectedLocationIndex()).controls; track $index) {
                    <div [formGroup]="getSectionFormGroup(selectedLocationIndex(), $index)" class="section-card">
                      <div class="section-card-header">
                        <h4>{{ t('section') }} {{ $index + 1 }}</h4>
                        <button class="icon-btn-danger" (click)="removeSection(selectedLocationIndex(), $index)" type="button">
                          <q-svg-wrapper iconPath="project-icons/trash" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                        </button>
                      </div>

                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>{{ t('title') }}</mat-label>
                        <input matInput formControlName="title">
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>{{ t('subtitle') }}</mat-label>
                        <input matInput formControlName="subtitle">
                      </mat-form-field>

                      <mat-form-field appearance="outline" class="full-width">
                        <mat-label>{{ t('description') }}</mat-label>
                        <textarea matInput formControlName="description" rows="4"></textarea>
                      </mat-form-field>

                      <!-- Images -->
                      <div class="images-container">
                        <div class="images-header">
                          <h5>{{ t('images') }}</h5>
                          <div class="image-actions">
                            <button class="secondary-bar-btn small-btn" (click)="addImageToSection(selectedLocationIndex(), $index)" type="button">
                              <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="16" height="16" viewBox="0 0 24 24"></q-svg-wrapper>
                              {{ t('addImage') }}
                            </button>
                            <span class="paste-hint" (click)="pasteImageFromClipboard(selectedLocationIndex(), $index)">
                              {{ t('pastePhoto') }}
                            </span>
                          </div>
                        </div>

                        <!-- Image Gallery -->
                        <div class="photos-row">
                          @for (image of getSectionImages(selectedLocationIndex(), $index).controls; track image.value.uuid; let imageIndex = $index) {
                            <div class="img-thumbnail">
                              <img [src]="image.value.url" alt="" />
                              <div class="icon-container edit">
                                <q-svg-wrapper
                                  class="small-icon pointer"
                                  [ngClass]="{'q-green': image.value.caption}"
                                  iconPath="project-icons/pencil"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  (click)="openEditImageDescriptionDialog(selectedLocationIndex(), $index, imageIndex)">
                                </q-svg-wrapper>
                              </div>
                              <div class="icon-container close">
                                <q-svg-wrapper
                                  class="small-icon pointer"
                                  iconPath="project-icons/close"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  (click)="removeImageByUuid(selectedLocationIndex(), $index, image.value.uuid)">
                                </q-svg-wrapper>
                              </div>
                            </div>
                          }
                        </div>
                      </div>
                    </div>
                  }
                </div>
              </div>
            }
          </div>

          <!-- Pathways -->
          <div class="form-card">
            <div class="card-header">
              <h2>{{ t('paths') }}</h2>
              <div class="display-flex-centered gap-8">
                <button class="primary-bar-btn small-btn" (click)="addPathway()" type="button">
                  <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                  {{ t('addPath') }}
                </button>
                <button class="secondary-bar-btn small-btn" (click)="importFromGPX()" type="button">
                  <q-svg-wrapper iconPath="project-icons/arrow-alt" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                  {{ t('importGPX') }}
                </button>
                <button
                  class="secondary-bar-btn small-btn"
                  (click)="openMergeModal()"
                  [disabled]="selectedPathwaysForMerge().length < 2"
                  [matTooltip]="t('mergePathways')"
                  type="button">
                  <q-svg-wrapper iconPath="project-icons/link" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                  {{ t('merge') }} ({{ selectedPathwaysForMerge().length }})
                </button>
              </div>
            </div>

            <!-- Pathways List - Grouped by Location -->
            @for (locationGroup of getAllPathwaysGroupedByLocation(); track locationGroup.locationIndex) {
              <div class="pathway-location-group" [class.active-location]="locationGroup.locationIndex === selectedLocationIndex()">
                <h4 class="location-group-header">{{ locationGroup.locationName }}</h4>

                @for (pathway of locationGroup.pathways; track pathway.value.uuid) {
                  <div class="pathway-item" [class.active]="currentPathway?.value.uuid === pathway.value.uuid">
                    <div class="pathway-info">
                      <input
                        type="checkbox"
                        [checked]="isPathwaySelectedForMerge(pathway)"
                        (change)="togglePathwayForMerge(pathway)"
                        [matTooltip]="t('selectForMerge')"
                        class="pathway-merge-checkbox">

                      <input
                        type="color"
                        [value]="pathway.value.color"
                        (change)="pathway.patchValue({ color: $any($event.target).value })"
                        class="pathway-color-picker">

                      <input
                        type="text"
                        [value]="pathway.value.name"
                        (input)="pathway.patchValue({ name: $any($event.target).value })"
                        [placeholder]="t('pathwayName')"
                        class="pathway-name-input">
                    </div>

                    <div class="pathway-actions-inline">
                      <!-- Transport mode buttons -->
                      <button
                        class="icon-btn transport-btn"
                        [class.active]="pathway.value.mappedToRoads && pathway.value.mappedPathProfile === 'walking'"
                        (click)="changePathwayMappingProfile(pathway, 'walking')"
                        [matTooltip]="t('walking')"
                        type="button">
                        <q-svg-wrapper iconPath="project-icons/walking-32" width="20" height="20" viewBox="0 0 32 32"></q-svg-wrapper>
                      </button>

                      <button
                        class="icon-btn transport-btn"
                        [class.active]="pathway.value.mappedToRoads && pathway.value.mappedPathProfile === 'cycling'"
                        (click)="changePathwayMappingProfile(pathway, 'cycling')"
                        [matTooltip]="t('cycling')"
                        type="button">
                        <q-svg-wrapper iconPath="project-icons/cycling-32" width="20" height="20" viewBox="0 0 32 32"></q-svg-wrapper>
                      </button>

                      <button
                        class="icon-btn transport-btn"
                        [class.active]="pathway.value.mappedToRoads && pathway.value.mappedPathProfile === 'driving'"
                        (click)="changePathwayMappingProfile(pathway, 'driving')"
                        [matTooltip]="t('driving')"
                        type="button">
                        <q-svg-wrapper iconPath="project-icons/car-32" width="20" height="20" viewBox="0 0 32 32"></q-svg-wrapper>
                      </button>

                      <!-- Simplify button -->
                      @if ((pathway.value.vertices?.length || 0) > 10) {
                        <button
                          class="icon-btn"
                          (click)="simplifyPathway(pathway)"
                          [matTooltip]="t('simplifyPathway')"
                          type="button">
                          <svg style="width: 20px; height: 20px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M3 12h18M12 5l7 7-7 7"/>
                          </svg>
                        </button>
                      }

                      <!-- Split button -->
                      <button
                        class="icon-btn"
                        (click)="openSplitModal(pathway)"
                        [matTooltip]="t('splitPathway')"
                        type="button">
                        <q-svg-wrapper iconPath="project-icons/link2" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                      </button>

                      <!-- Cut dropdown -->
                      <button
                        class="icon-btn"
                        [matMenuTriggerFor]="cutMenu"
                        [matTooltip]="t('cutPathway')"
                        type="button">
                        <q-svg-wrapper iconPath="project-icons/distance-arrows" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                      </button>
                      <mat-menu #cutMenu="matMenu">
                        <button mat-menu-item (click)="openCutModal(pathway, 'start')">
                          {{ t('cutFromStart') }}
                        </button>
                        <button mat-menu-item (click)="openCutModal(pathway, 'end')">
                          {{ t('cutFromEnd') }}
                        </button>
                        <button mat-menu-item (click)="openCutModal(pathway, 'section')">
                          {{ t('cutSection') }}
                        </button>
                      </mat-menu>

                      <button class="icon-btn-danger" (click)="removePathway(pathway)" [matTooltip]="t('delete')" type="button">
                        <q-svg-wrapper iconPath="project-icons/x" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                      </button>

                      <button
                        [class.icon-btn]="currentPathway?.value.uuid !== pathway.value.uuid"
                        [class.icon-btn-success]="currentPathway?.value.uuid === pathway.value.uuid"
                        (click)="startEditPathway(pathway)"
                        [matTooltip]="currentPathway?.value.uuid === pathway.value.uuid ? t('stopEditing') : t('edit')"
                        type="button">

                        @if (currentPathway?.value.uuid !== pathway.value.uuid) {
                          <q-svg-wrapper
                            iconPath="project-icons/pencil"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24">
                          </q-svg-wrapper>
                        } @else {
                          <q-svg-wrapper
                            iconPath="project-icons/check"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24">
                          </q-svg-wrapper>
                        }
                      </button>

                      <button class="icon-btn" (click)="exportPathway(pathway)" [matTooltip]="t('export')" type="button">
                        <q-svg-wrapper iconPath="project-icons/share" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                      </button>
                    </div>
                  </div>
                }
              </div>
            }
          </div>

          <!-- Points of Interest (POI) -->
          <div class="form-card">
            <div class="card-header">
              <h2>{{ t('pointsOfInterest') }}</h2>
            </div>

            <!-- POI List - Grouped by Location -->
            @for (location of locations.controls; track $index) {
              @if (getLocationPOIs($index).length > 0 || selectedLocationIndex() === $index) {
                <div class="poi-location-group" [class.active-location]="$index === selectedLocationIndex()">
                  <div class="location-group-header-row">
                    <h4 class="location-group-header">{{ location.get('name')?.value || ('location' | transloco) + ' ' + ($index + 1) }}</h4>
                    @if (selectedLocationIndex() === $index) {
                      <button class="primary-bar-btn small-btn" (click)="addPOI($index)" type="button">
                        <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                        {{ t('addPOI') }}
                      </button>
                    }
                  </div>

                  @for (poi of getLocationPOIs($index); track poi.uuid) {
                    <div class="poi-item">
                      <div class="poi-info">
                        <div class="poi-icon">
                          <img [src]="'assets/maki/' + poi.icon + '.svg'" [alt]="poi.icon" width="24" height="24">
                        </div>
                        <div class="poi-details">
                          <div class="poi-name">{{ poi.name }}</div>
                          @if (poi.description) {
                            <div class="poi-description">{{ poi.description }}</div>
                          }
                        </div>
                      </div>

                      <div class="poi-actions">
                        <button class="icon-btn" (click)="editPOI($index, poi)" [matTooltip]="t('edit')" type="button">
                          <q-svg-wrapper iconPath="project-icons/pencil" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                        </button>

                        <button class="icon-btn-danger" (click)="removePOI($index, poi.uuid!)" [matTooltip]="t('delete')" type="button">
                          <q-svg-wrapper iconPath="project-icons/x" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                        </button>
                      </div>
                    </div>
                  }

                  @if (getLocationPOIs($index).length === 0 && selectedLocationIndex() === $index) {
                    <div class="empty-state">
                      <p>{{ t('noPOIsYet') }}</p>
                    </div>
                  }
                </div>
              }
            }
          </div>

          <!-- Obelisks & Quests -->
          <div class="form-card">
            <div class="card-header">
              <h2>{{ t('obelisksAndQuests') }}</h2>
            </div>

            <!-- Clusters List - Grouped by Location -->
            @for (location of locations.controls; track location; let index = $index) {
              @if (getLocationClusters(index).length > 0 || selectedLocationIndex() === index) {
                <div class="cluster-location-group" [class.active-location]="index === selectedLocationIndex()">
                  <div class="location-group-header-row">
                    <h4 class="location-group-header">{{ location.get('name')?.value || ('location' | transloco) + ' ' + (index + 1) }}</h4>
                    @if (selectedLocationIndex() === index) {
                      <button class="primary-bar-btn small-btn" (click)="openClusterSearch(index)" type="button">
                        <q-svg-wrapper iconPath="project-icons/add" iconId="a" width="18" height="18" viewBox="0 0 24 24"></q-svg-wrapper>
                        {{ t('addCluster') }}
                      </button>
                    }
                  </div>

                  @for (cluster of getLocationClusters(index); track cluster) {
                    <div class="cluster-item">
                      <div class="cluster-info">
                        <div class="cluster-icon">
                          @if (isQuestMetadata(cluster)) {
                            <q-svg-wrapper iconPath="project-icons/map" width="24" height="24" viewBox="0 0 20 20"></q-svg-wrapper>
                          } @else {
                            <q-svg-wrapper iconPath="project-icons/place" width="24" height="24" viewBox="0 0 24 24"></q-svg-wrapper>
                          }
                        </div>
                        <div class="cluster-details">
                          <div class="cluster-name">{{ cluster.name }}</div>
                          <div class="cluster-type">{{ isQuestMetadata(cluster) ? t('quest') : t('obelisk') }}</div>
                        </div>
                      </div>

                      <div class="cluster-actions">
                        <button class="icon-btn-danger" (click)="removeClusterFromLocation(index, cluster)" [matTooltip]="t('delete')" type="button">
                          <q-svg-wrapper iconPath="project-icons/x" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
                        </button>
                      </div>
                    </div>
                  }

                  @if (getLocationClusters(index).length === 0 && selectedLocationIndex() === index) {
                    <div class="empty-state">
                      <p>{{ t('noClustersYet') }}</p>
                    </div>
                  }
                </div>
              }
            }
          </div>
        </form>
      </div>

      <!-- Right Side: Map -->
      <div class="map-section">
        <div class="map-controls">
          <div class="map-controls-row">
            <button class="icon-btn" (click)="goToMyLocation()" [matTooltip]="t('myLocation')">
              <q-svg-wrapper iconPath="project-icons/my-location" width="24" height="24" viewBox="0 0 24 24"></q-svg-wrapper>
            </button>

            <button class="icon-btn" (click)="toggleMagStyle()" [matTooltip]="t('changeMapStyle')">
              <q-svg-wrapper iconPath="project-icons/layers" width="24" height="24" viewBox="0 0 24 24"></q-svg-wrapper>
            </button>
          </div>
        </div>

        <div class="map-container">
          <div id="map-div" class="map-display"></div>
        </div>
      </div>
    </div>
  }
</div>

<!-- Image Description Edit Modal -->
@if (editingImageId) {
  <q-modal [title]="'editImageDescription' | transloco" [size]="'medium'" [footerButtons]="editDescriptionModalButtons" (closeEvent)="closeEditImageDescriptionDialog()">
    <mat-form-field appearance="fill" class="form-field mat-text-area" color="accent">
      <mat-label>{{ 'description' | transloco }}</mat-label>
      <textarea matInput [(ngModel)]="editingImageDescription" rows="5" maxlength="500"></textarea>
    </mat-form-field>
  </q-modal>
}

<!-- POI Edit Side Panel -->
@if (showPOIModal() && editingPOI) {
  <div class="poi-edit-panel">
    <div class="panel-header">
      <h3>{{ (editingPOI.uuid ? 'editPOI' : 'addPOI') | transloco }}</h3>
      <button class="icon-btn" (click)="closePOIModal()">
        <q-svg-wrapper iconPath="project-icons/close" width="20" height="20" viewBox="0 0 24 24"></q-svg-wrapper>
      </button>
    </div>

    <div class="panel-content">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{ 'name' | transloco }}</mat-label>
        <input matInput [(ngModel)]="editingPOI.name" required>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{ 'description' | transloco }}</mat-label>
        <textarea matInput [(ngModel)]="editingPOI.description" rows="3"></textarea>
      </mat-form-field>

      <mat-form-field appearance="outline" class="full-width">
        <mat-label>{{ 'website' | transloco }}</mat-label>
        <input matInput [(ngModel)]="editingPOI.website" type="url">
      </mat-form-field>

      <div class="icon-selector">
        <label>{{ 'icon' | transloco }}</label>
        <div class="icon-grid">
          @for (iconName of availableMakiIcons; track iconName) {
            <div
              class="icon-option"
              [class.selected]="editingPOI.icon === iconName"
              (click)="selectPOIIcon(iconName)">
              <img [src]="'assets/maki/' + iconName + '.svg'" [alt]="iconName" width="24" height="24">
              <span class="icon-label">{{ iconName }}</span>
            </div>
          }
        </div>
      </div>

      <div class="coordinates-section">
        <h4>{{ 'location' | transloco }}</h4>
        <div class="coordinates-inputs">
          <mat-form-field appearance="outline">
            <mat-label>{{ 'latitude' | transloco }}</mat-label>
            <input matInput [(ngModel)]="editingPOI.coords.lat" type="number" step="0.000001">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>{{ 'longitude' | transloco }}</mat-label>
            <input matInput [(ngModel)]="editingPOI.coords.lng" type="number" step="0.000001">
          </mat-form-field>
        </div>
        <p class="hint-text">{{ 'clickMapToSetLocation' | transloco }}</p>
      </div>
    </div>

    <div class="panel-footer">
      <button class="secondary-btn" (click)="closePOIModal()">
        {{ 'cancel' | transloco }}
      </button>
      <button class="primary-btn" (click)="submitPOI()">
        {{ 'save' | transloco }}
      </button>
    </div>
  </div>
}

<!-- Cluster Search Modal -->
@if (showClusterModal()) {
  <q-modal [title]="'addCluster' | transloco" [size]="'medium'" (closeEvent)="closeClusterModal()">
    <div class="cluster-search-content" *transloco="let t">
      <mat-form-field appearance="outline" class="full-width margin-top-12">
        <mat-label>{{ t('searchQuestsAndObelisks') }}</mat-label>
        <input matInput [(ngModel)]="clusterSearchQuery" (input)="searchClusters()" [placeholder]="t('typeToSearch')">
      </mat-form-field>

      @if (searchingClusters()) {
        <div class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>{{ t('searching') }}...</p>
        </div>
      }

      @if (!searchingClusters() && clusterSearchResults.length > 0) {
        <div class="search-results">
          @for (cluster of clusterSearchResults; track (isQuestMetadata(cluster) ? cluster.id : cluster.uuid)) {
            <div class="cluster-search-item" (click)="addClusterToLocation(cluster)">
              <div class="cluster-search-icon">
                @if (isQuestMetadata(cluster)) {
                  <q-svg-wrapper iconPath="project-icons/map" width="24" height="24" viewBox="0 0 20 20"></q-svg-wrapper>
                } @else {
                  <q-svg-wrapper iconPath="project-icons/place" width="24" height="24" viewBox="0 0 24 24"></q-svg-wrapper>
                }
              </div>
              <div class="cluster-search-info">
                <div class="cluster-search-name">{{ cluster.name }}</div>
                <div class="cluster-search-type">{{ isQuestMetadata(cluster) ? t('quest') : t('obelisk') }}</div>
              </div>
            </div>
          }
        </div>
      }

      @if (!searchingClusters() && clusterSearchQuery && clusterSearchResults.length === 0) {
        <div class="empty-state">
          <p>{{ t('noResultsFound') }}</p>
        </div>
      }
    </div>
  </q-modal>
}

<!-- Merge Pathways Modal -->
@if (showMergeModal()) {
  <q-modal [title]="'mergePathways' | transloco" [size]="'small'" (closeEvent)="closeMergeModal()">
    <div class="merge-modal-content" *transloco="let t">
      <p>{{ t('mergePathwaysConfirmation', { count: selectedPathwaysForMerge().length }) }}</p>

      <div class="selected-pathways-list">
        @for (pathway of selectedPathwaysForMerge(); track pathway.value.uuid) {
          <div class="pathway-preview-item">
            <div class="pathway-color-dot" [style.background-color]="pathway.value.color"></div>
            <span>{{ pathway.value.name || t('unnamedPathway') }}</span>
          </div>
        }
      </div>

      <div class="modal-actions">
        <button class="secondary-btn" (click)="closeMergeModal()">
          {{ t('cancel') }}
        </button>
        <button class="primary-btn" (click)="mergePathways()">
          {{ t('merge') }}
        </button>
      </div>
    </div>
  </q-modal>
}

<!-- Split Pathway Modal -->
@if (showSplitModal() && pathwayForOperation()) {
  <q-modal [title]="'splitPathway' | transloco" [size]="'medium'" [dock]="'left'" [overlay]="false" (closeEvent)="closeSplitModal()">
    <div class="split-modal-content" *transloco="let t">
      <p>{{ t('splitPathwayDescription') }}</p>

      <div class="pathway-info-display">
        <div class="pathway-color-dot" [style.background-color]="pathwayForOperation()!.value.color"></div>
        <span>{{ pathwayForOperation()!.value.name || t('unnamedPathway') }}</span>
        <span class="vertex-count">({{ calculatePathwayDistance(pathwayForOperation()!.value.vertices).toFixed(2) }} {{ t('km') }})</span>
      </div>

      <div class="split-control">
        <label>{{ t('splitAtDistance') }}: {{ getDistanceToVertex(pathwayForOperation()!.value.vertices, splitVertexIndex()).toFixed(2) }} {{ t('km') }}</label>
        <input
          type="range"
          [min]="1"
          [max]="(pathwayForOperation()!.value.vertices?.length || 2) - 2"
          [value]="splitVertexIndex()"
          (input)="onSplitVertexChange(+$any($event.target).value)"
          class="vertex-slider">
        <div class="slider-labels">
          <span>0 {{ t('km') }}</span>
          <span>{{ calculatePathwayDistance(pathwayForOperation()!.value.vertices).toFixed(2) }} {{ t('km') }}</span>
        </div>
      </div>

      <div class="modal-actions">
        <button class="secondary-btn" (click)="closeSplitModal()">
          {{ t('cancel') }}
        </button>
        <button class="primary-btn" (click)="splitPathway()">
          {{ t('split') }}
        </button>
      </div>
    </div>
  </q-modal>
}

<!-- Cut Pathway Modal -->
@if (showCutModal() && pathwayForOperation()) {
  <q-modal [title]="'cutPathway' | transloco" [size]="'medium'" (closeEvent)="closeCutModal()">
    <div class="cut-modal-content" *transloco="let t">
      @if (cutMode() === 'start') {
        <p>{{ t('cutFromStartDescription') }}</p>
      } @else if (cutMode() === 'end') {
        <p>{{ t('cutFromEndDescription') }}</p>
      } @else {
        <p>{{ t('cutSectionDescription') }}</p>
      }

      <div class="pathway-info-display">
        <div class="pathway-color-dot" [style.background-color]="pathwayForOperation()!.value.color"></div>
        <span>{{ pathwayForOperation()!.value.name || t('unnamedPathway') }}</span>
        <span class="vertex-count">({{ calculatePathwayDistance(pathwayForOperation()!.value.vertices).toFixed(2) }} {{ t('km') }})</span>
      </div>

      @if (cutMode() === 'start' || cutMode() === 'end') {
        <div class="cut-control">
          <label>
            @if (cutMode() === 'start') {
              {{ t('cutUpToDistance') }}: {{ getDistanceToVertex(pathwayForOperation()!.value.vertices, cutStartVertexIndex()).toFixed(2) }} {{ t('km') }}
            } @else {
              {{ t('cutFromDistance') }}: {{ getDistanceToVertex(pathwayForOperation()!.value.vertices, (pathwayForOperation()!.value.vertices?.length || 2) - 1 - cutEndVertexIndex()).toFixed(2) }} {{ t('km') }}
            }
          </label>
          <input
            type="range"
            [min]="0"
            [max]="(pathwayForOperation()!.value.vertices?.length || 2) - 2"
            [value]="cutMode() === 'start' ? cutStartVertexIndex() : ((pathwayForOperation()!.value.vertices?.length || 2) - 1 - cutEndVertexIndex())"
            (input)="onCutVertexChange(+$any($event.target).value)"
            class="vertex-slider">
          <div class="slider-labels">
            <span>0 {{ t('km') }}</span>
            <span>{{ calculatePathwayDistance(pathwayForOperation()!.value.vertices).toFixed(2) }} {{ t('km') }}</span>
          </div>
        </div>
      } @else {
        <div class="cut-section-controls">
          <div class="cut-control">
            <label>{{ t('startDistance') }}: {{ getDistanceToVertex(pathwayForOperation()!.value.vertices, cutStartVertexIndex()).toFixed(2) }} {{ t('km') }}</label>
            <input
              type="range"
              [min]="0"
              [max]="(pathwayForOperation()!.value.vertices?.length || 2) - 2"
              [value]="cutStartVertexIndex()"
              (input)="onCutStartVertexChange(+$any($event.target).value)"
              class="vertex-slider">
          </div>

          <div class="cut-control">
            <label>{{ t('endDistance') }}: {{ getDistanceToVertex(pathwayForOperation()!.value.vertices, cutEndVertexIndex()).toFixed(2) }} {{ t('km') }}</label>
            <input
              type="range"
              [min]="1"
              [max]="(pathwayForOperation()!.value.vertices?.length || 2) - 1"
              [value]="cutEndVertexIndex()"
              (input)="onCutEndVertexChange(+$any($event.target).value)"
              class="vertex-slider">
          </div>
        </div>
      }

      <div class="modal-actions">
        <button class="secondary-btn" (click)="closeCutModal()">
          {{ t('cancel') }}
        </button>
        <button class="primary-btn" (click)="cutPathway()">
          {{ t('cut') }}
        </button>
      </div>
    </div>
  </q-modal>
}
